import { MarkdownClipboardProvider } from "@storytell/markdown";
import type {
  StreamingBlock,
  StreamingChat,
  StreamingChatStatus,
  StreamingMessage,
  StreamingOperation,
  StreamingPromptAttachment,
} from "@storytell/streaming";
import type { FeatureUse } from "@storytell/types";
import type { CanvasTarget } from "@storytell/ui/transcript-media";
import { createContext, type JSX, useContext } from "solid-js";
import type { BlockRegistry } from "./blockRegistry";

type PromptRichText = StreamingMessage["prompt"]["richText"];

/**
 * Asset-store snapshot a citation renderer reads for its chrome (display name,
 * deleted state). bluesky fills it from `wire.assets`; chief omits the store
 * accessor entirely and the renderers fall back to the citation's own metadata.
 */
export interface AssetCitationInfo {
  name?: string;
  filename?: string;
  /** Lifecycle state from the app's asset store; "DELETED" disables open/download. */
  state?: string;
}

/**
 * Reactive structural reads over the streaming store. Renderers call these
 * instead of reaching into an app-specific wire. bluesky backs them with
 * `wire.stream.*`; chief backs them with its own `@storytell/streaming` store.
 *
 * Block text content does not flow through here — it stays the module-level
 * delta singleton in `@storytell/streaming` (subscribeToBlockDeltas /
 * getBlockContent), which is identical on both sides.
 */
export interface ChatStoreAccessor {
  block(id: string): StreamingBlock | undefined;
  operation(id: string): StreamingOperation | undefined;
  message(id: string): StreamingMessage | undefined;
  chat(id: string): StreamingChat | undefined;
  /** Derived streaming status for a chat (idle/streaming/…); used for end-turn header state. */
  chatStatus?(id: string): StreamingChatStatus | undefined;
  /**
   * Fresh display name for an asset, when the app has an asset store
   * (bluesky → `wire.assets`). Renderers fall back to the citation's own
   * metadata (filename/title) when this is absent or returns undefined.
   */
  assetName?(assetId: string): string | undefined;
  /**
   * Reactive asset-store entry for artifact/KB citation chrome. Absent on
   * surfaces without an asset store (chief), where the renderer treats the
   * asset as not-yet-resolved and leans on the citation's own metadata.
   */
  asset?(assetId: string): AssetCitationInfo | undefined;
  /** Whether an asset fetch terminally failed (bluesky → `wire.assets.hasFailed`). */
  assetFailed?(assetId: string): boolean;
}

interface OpenAssetArgs {
  assetId: string;
}

interface DownloadAssetArgs {
  assetId: string;
  filename: string;
}

interface NavigateToChatArgs {
  chatId: string;
  messageId: string;
  projectId?: string;
}

interface ConfirmActionArgs {
  actionId: string;
  decision: "approve" | "approve_always" | "reject";
  notifyAttendees?: boolean;
  edited?: unknown;
}

/** Real status of one confirm-then-execute action, from the host's list endpoint. */
interface ToolActionLiveStatus {
  id: string;
  status: string;
  /** Whether the reading user may accept it; absent on hosts that don't report it. */
  approvable?: boolean;
  url?: string;
  error?: string;
  executedAt?: string;
  /** The action failed because the server rejected the user's own MCP connection. */
  reconnect?: boolean;
  /** The tool-approval preference key the action is gated on (e.g. `notion.write`). */
  op?: string;
  /** Whether the reading user may approve it with "always" and turn that key on. */
  alwaysApprovable?: boolean;
  /** The action ran without a card because of the reading user's saved tool permission. */
  autoApproved?: boolean;
}

export type IntegrationProvider = "google" | "slack" | "notion" | "linear" | "github";

/**
 * Backend callbacks a renderer may invoke. `openAsset` is the load-bearing one
 * (KB/artifact citations); the rest are feature-detected (`rt.actions.x?.(...)`)
 * so a surface that hasn't implemented one can still import the renderer.
 */
export interface ChatActions {
  /** Open a cited/knowledge asset (bluesky → side panel; chief → App.OpenCitation). */
  openAsset: (args: OpenAssetArgs) => void;
  /** Download an asset's source file (bluesky → signed URL; chief → App.DownloadAsset). */
  downloadAsset?: (args: DownloadAssetArgs) => Promise<void> | void;
  /**
   * Read URLs for a message's attachments, keyed by artifact id. Called once in component scope, so
   * the host can back it with a reactive cache. Adding to knowledge lands after the send, so the host
   * looks again while an attachment that asked for it has no asset id yet. Attachments the
   * reader can't see are left out, and the map is empty until the first lookup lands. Absent → names only.
   */
  attachmentPreviews?: (
    attachments: () => StreamingPromptAttachment[]
  ) => () => Record<string, { url?: string; assetId?: string }>;
  /**
   * Add a sent attachment to the Library, resolving false when that failed. The host refreshes
   * attachmentPreviews itself, which is what lights the tile. Absent → the tile only reports it.
   */
  addAttachmentToLibrary?: (artifactId: string) => Promise<boolean>;
  /** Put an image on the clipboard as a picture, rejecting when that failed. Absent → no copy control. */
  copyImage?: (url: string) => Promise<void>;
  /** Open a web source in the browser (bluesky → window.open; chief → App.OpenURL). */
  openExternalUrl?: (url: string) => void;
  /**
   * Write text to the clipboard, resolving false when the write failed. A host
   * whose origin is not a secure context (chief's wails:// webview) has to
   * supply this — the browser API rejects there. Absent → the browser API.
   */
  copyText?: (text: string) => Promise<boolean> | boolean;
  /** Navigate to a past-chat message (bluesky → router; chief → omitted). */
  navigateToChat?: (args: NavigateToChatArgs) => void;
  /**
   * Anchor href for a past-chat citation, so the renderer keeps native link
   * semantics (middle-click, open-in-new-tab). bluesky → `urls.chatHref`;
   * chief → omitted (renders a non-link).
   */
  chatCitationHref?: (args: NavigateToChatArgs) => string | undefined;
  /** Re-run a failed turn (ErrorRenderer). */
  retryMessage?: (args: { promptText: string; richText?: PromptRichText }) => void;
  /**
   * Resend the failed turn asking the backend router to try a different vendor.
   * A surface without this shows only Try Again.
   */
  retryWithDifferentModel?: (args: { promptText: string; messageId: string; richText?: PromptRichText }) => void;
  /** Open a generated-output content block (bluesky → side panel; chief → the chat's web-console page in the browser). */
  openContentBlock?: (blockId: string) => void;
  /** M3 tool actions. Resolves to the resulting status ("queued" on approval). */
  confirmAction?: (args: ConfirmActionArgs) => Promise<string>;
  /** Real statuses for a chat's confirm cards (persisted cards render pending). */
  fetchToolActions?: (chatId: string) => Promise<ToolActionLiveStatus[]>;
  /**
   * Calls back with an artifact's id when the server reports a change to it,
   * for any chat; returns the unsubscribe. Delivery can miss events, so cards
   * waiting on an async artifact still poll, just slowly. Absent → they poll fast.
   */
  subscribeArtifactChanges?: (onChange: (artifactId: string) => void) => () => void;
  /** Submit a prompt into the current chat (ask_user replies; chief → sendFollowUp). */
  sendPrompt?: (text: string) => void;
  /**
   * Send a user message into a specific chat once an action the user approved
   * here has executed, so the next turn picks up the result without a
   * re-prompt. The host drops it when that chat is not the one it has open.
   * Absent → approvals never continue the chat on their own.
   */
  continueChat?: (args: { chatId: string; text: string }) => void;
  /**
   * Prefill the composer with text WITHOUT submitting (ask_user "edit option",
   * follow-up chips). bluesky → prompt bar insert; chief → chat panel textarea.
   */
  insertPromptText?: (text: string) => void;
  /** Re-stick the view to the streaming tail (heartbeat pill click). */
  resumeAutoscroll?: () => void;
  connectProvider?: (provider: IntegrationProvider) => void;
  /** Open the app's memories surface (memory card "View"; bluesky → router). */
  openMemories?: (scope: "user" | "project" | "organization") => void;
  /**
   * Anchor href for the provider integration settings the setup-required admin
   * CTA links to. bluesky computes the org-scoped URL; absent → CTA hidden.
   */
  integrationSettingsHref?: (provider: IntegrationProvider) => string | undefined;
  /** Absent → the confirm card's hint is plain text. */
  openToolPermissions?: () => void;
  /** Open a generated artifact in the host's viewer pane (bluesky → extended pane). */
  openArtifact?: (args: OpenArtifactArgs) => void;
  /**
   * Open a document the agent created in a live session's workspace (chief →
   * the artifact dock tab). Absent on hosts with no session workspace, which
   * leaves the chat card an inert receipt.
   */
  openSessionDocument?: (args: { artifactId: string }) => void;
  /** Save client-generated text to disk (bluesky → blob download; chief → native dialog). */
  saveGeneratedFile?: (args: { filename: string; mimeType: string; text: string }) => Promise<void> | void;
  /** Persist a crafted skill to a scope; resolves false on failure. */
  saveSkill?: (input: SaveSkillInput) => Promise<boolean>;
  /** Scopes the named skill is already saved in (crafter card sync). */
  listSkillScopes?: (name: string) => Promise<string[]>;
  /**
   * Signed display URLs for generated images/charts (bluesky → images client +
   * cache; chief → App.GetImageSignedURLs). Order matches the input. Also the
   * status lookup for async artifacts (videos): `status` is "pending", "ready"
   * or "failed", absent means ready (sync artifacts carry none), and only a
   * ready entry has a `url`.
   */
  resolveImageUrls?: (locations: MediaStorageLocation[]) => Promise<ResolvedMediaUrl[]>;
  /** Signed URLs for tool artifacts (report/presentation bodies) by artifact ID. */
  resolveArtifactUrls?: (artifactIds: string[]) => Promise<ResolvedArtifactUrl[]>;
  /**
   * Text of a generated blob (report markdown). Hosts whose webview may not
   * fetch (chief) route the download through their backend process.
   */
  fetchGeneratedText?: (location: MediaStorageLocation) => Promise<string>;
  /**
   * Called once per turn: resolves once the suggestions are generated, or []
   * when none arrive (chief → device proxy that waits server-side; bluesky uses
   * its SSE-driven store instead). `signal` aborts when the chips unmount so the
   * host can release the held request. Absent → no fetched chips.
   */
  fetchFollowUps?: (chatId: string, messageId: string, signal: AbortSignal) => Promise<FollowUpSuggestion[]>;
  /**
   * Download a generated image/chart object to a file. bluesky fetches the
   * signed URL and triggers a browser download; chief routes the bytes through
   * its Go process (the webview can't fetch). Absent → no download affordance.
   */
  downloadMedia?: (args: { location: MediaStorageLocation; filename: string }) => Promise<void>;
  /**
   * Session canvases to offer for placing a generated image, and the action
   * that places it. `targets()` returning undefined hides the affordance
   * (no session, or the host has no canvas surface); `undefined` for `targetId`
   * means "New canvas".
   */
  addToCanvas?: {
    targets(): CanvasTarget[] | undefined;
    add(args: { location: MediaStorageLocation; filename: string; targetId?: string }): Promise<void>;
  };
  /**
   * Render a presentation to a PDF and save it — the same html-to-pdf export
   * the web app runs, so the file matches. chief proxies to the control plane's
   * headless-Chrome route. Absent → no PDF affordance on the deck.
   */
  exportPresentationPdf?: (args: { html: string; title: string; filename: string }) => Promise<void>;
  trackFeature?: (use: FeatureUse) => void;
}

export interface MediaStorageLocation {
  bucket: string;
  key: string;
}

export interface FollowUpCapabilityRef {
  name: string;
  displayName: string;
}

export interface FollowUpSuggestion {
  text: string;
  icon?: string;
  capabilities?: {
    agents?: (string | FollowUpCapabilityRef)[];
    skills?: (string | FollowUpCapabilityRef)[];
  };
}

interface ResolvedMediaUrl {
  bucket: string;
  key: string;
  url?: string;
  error?: string;
  status?: string;
}

interface ResolvedArtifactUrl {
  artifactId: string;
  url?: string;
}

interface OpenArtifactArgs {
  operationId: string;
  blockId: string;
  title?: string;
}

interface SaveSkillInput {
  name: string;
  displayName: string;
  description: string;
  content: string;
  scope: "user" | "project";
  context?: "agent";
  agent?: string;
  category: "skill" | "persona";
}

/**
 * Capability flags let a host turn on rendering the shared renderer otherwise
 * keeps off (chief: inline media instead of the preview-card jump).
 */
export interface ChatCapabilities {
  /**
   * Render generated images inline in the thread instead of a preview card,
   * even when `openContentBlock` exists (chief: the browser jump is a fallback,
   * not a viewer).
   */
  inlineMedia?: boolean;
}

/**
 * Optional UI chrome the dispatch/operation renderers reach for. Every field is
 * optional and feature-detected — chief omits most of these (its renderers fall
 * back to local state / always-visible affordances).
 */
export interface ChatUiHooks {
  /** Public/shared read-only view — hide edit/save/download affordances. */
  isReadonly?: () => boolean;
  /** Learn-more/help doc links (bluesky Help Tooltips setting). Absent → shown. */
  showHelpLinks?: () => boolean;
  /** Stream resume in progress after a network drop (bluesky SSE). Absent → never. */
  isReconnecting?: () => boolean;
  /** Whether the reader may add a sent attachment to the Library. Absent → no add control. */
  canAddToLibrary?: () => boolean;
  /** Org-owner check for setup-required admin CTAs. Absent → non-admin copy. */
  isOrgAdmin?: () => boolean;
  /**
   * i18n key for an auto-approvable operation (the host owns the approval
   * catalog). Absent or unknown op → the raw op string is shown.
   */
  autoApproveOpLabelKey?: (op: string) => string | undefined;
  /**
   * Provider OAuth connect flow with connecting state, for the connect cards.
   * Called in component scope (may use Solid primitives). Absent → the card
   * falls back to `actions.connectProvider`, or hides its button entirely.
   */
  useProviderConnect?: (provider: "google" | "slack") => {
    connect: () => void | Promise<void>;
    isConnecting: () => boolean;
    error?: () => string | null;
  };
  /**
   * MCP OAuth connect flow, for a failed MCP action whose server rejected the
   * user's connection. Called in component scope (may use Solid primitives).
   * Absent → no reconnect button; the card shows only the failure text.
   */
  useMcpConnect?: () => {
    connect: (serverId: string) => void | Promise<void>;
    isConnecting: () => boolean;
    error?: () => string | null;
  };
  /** Loaded skills catalog, for resolving skill/persona labels (bluesky only). */
  skills?: () => Array<{ name: string; displayName: string; category: string }>;
  /**
   * Host's activity-sidebar integration (bluesky moves the timeline into the
   * side panel). Absent on chief, where the timeline always renders inline.
   * `focusOperationId` opens and scrolls to that operation's card in the panel.
   */
  activitySidebar?: { inSidebar(): boolean; moveToSidebar(focusOperationId?: string): void };
  /**
   * Persistent per-chat collapse state for an operation/section. Returns a
   * reactive getter + setter. bluesky → localStorage; chief → in-memory.
   * Called in component scope, so it may use Solid primitives internally.
   */
  usePersistentExpanded?: (
    chatId: string | undefined,
    key: string,
    initial: boolean
  ) => readonly [() => boolean, (value: boolean, isManual?: boolean) => void, () => boolean];
}

/** Args the operation-header action-row slot receives (copy / save-to-KB / download affordances). */
export interface OperationActionsArgs {
  downloadContent: () => string;
  downloadSubtitle?: () => string | undefined;
  downloadTooltip?: string;
  copyTooltip?: string;
  addTooltip?: string;
  title: string;
  context?: string;
}

/**
 * Optional render slots for app-only widgets the shared chrome leaves holes for.
 * bluesky fills them; chief omits them and the chrome renders without. Each is a
 * render function called within the host component's reactive scope.
 */
export interface ChatSlots {
  /** Hover action row on an operation header (copy / save-to-KB / download). */
  operationActions?: (args: OperationActionsArgs) => JSX.Element;
  /** Multi-agent task operation (bluesky's TaskOperationRenderer). */
  taskOperation?: (args: { operation: StreamingOperation; chatId: string; compact?: boolean }) => JSX.Element;
  /** Slack channel-connect widget under the connect-Slack card (bluesky only). */
  slackChannelsConnect?: () => JSX.Element;
  /** Download/copy affordances for a rendered results table (bluesky's TableDownloadButtons). */
  tableActions?: (args: { tableElement: HTMLTableElement; chatId?: string }) => JSX.Element;
  /** Structured search-result card (bluesky's StructuredSearchItemRenderer). */
  structuredSearchItem?: (args: {
    query: string;
    structuredResults: NonNullable<StreamingBlock["structuredResults"]>;
    chatId: string;
    itemKey: string;
    isProcessing: boolean;
  }) => JSX.Element;
  /** Tool-match highlights shown under a planning operation. */
  toolMatches?: (args: { chatId: string }) => JSX.Element;
  /**
   * Wraps an answer operation's blocks with the host's interactive answer chrome
   * (bluesky's select-text bolt bar / answer action row). Absent on chief, where
   * the answer blocks render plainly.
   */
  answerChrome?: (args: {
    operation: StreamingOperation;
    chatId: string;
    answerBlockId: () => string | undefined;
    blocksContainerRef: () => HTMLElement | undefined;
    children: JSX.Element;
  }) => JSX.Element;
  /**
   * Body of the activity timeline — the operation rows. bluesky → AgentDetailView
   * (rich agent/task detail); chief → a simple loop over the shared
   * OperationRenderer. The shared timeline owns the grouped header + collapse.
   *
   * The slot is invoked inside a tracked scope, so `operations` is an accessor:
   * the operation list is a fresh array on every stream event, and reading it
   * here would dispose and rebuild the whole body — losing any expand state the
   * body holds in local signals. Hosts must forward it lazily.
   */
  activityDetail?: (args: {
    operations: () => Array<{ operation: StreamingOperation; index: number }>;
    chatId: string;
    messageId: string;
    /** `undefined` means the user has not touched expand-all, so each row decides for itself. */
    allExpanded: boolean | undefined;
    noTruncation: boolean;
    /** An accessor for the same reason as `operations`: the operation whose card to open and scroll to. */
    focusOperationId: () => string | undefined;
  }) => JSX.Element;

  /** Echo of the user's prompt above the assistant turn. */
  promptAck?: (args: {
    prompt: NonNullable<StreamingMessage["prompt"]>;
    messageId: string;
    chatId: string;
  }) => JSX.Element;
  /** Error state when a message failed to load. */
  messageLoadError?: () => JSX.Element;
  /**
   * Pinned "action required" section + the per-message hoisting it implies.
   * Returns the rendered section plus the set of block ids hoisted out of the
   * timeline (so the shared renderer can drop their operations). bluesky →
   * confirm-action flow; chief omits it. Called in the renderer's reactive scope.
   */
  messageActionRequired?: (args: {
    messageId: string;
    chatId: string;
    activityOps: () => Array<{ operation: StreamingOperation; index: number }>;
  }) => { hoistedBlockIds: () => Set<string>; section: () => JSX.Element };
  /**
   * Rich editor for action-config confirm cards (schedule pickers, recipients).
   * Absent → the generic key-value edit form. `payload` is the parsed
   * action_config payload; hosts cast to their schema type.
   */
  confirmActionEditor?: (args: {
    payload: unknown;
    isSubmitting: boolean;
    error: unknown;
    onApprove: (editedParams: Record<string, unknown>) => void;
    onCancel: () => void;
  }) => JSX.Element | null;
  /**
   * Card collecting generated artifacts (images, charts, reports, presentations).
   * Invoked in a tracked scope, so `operations` is an accessor: reading it here
   * would rebuild the card, and refetch every artifact it shows, on each change
   * to the list.
   */
  generatedArtifacts?: (args: {
    operations: () => Array<{ operation: StreamingOperation; index: number }>;
    chatId: string;
  }) => JSX.Element;
  /** Live heartbeat indicator for the last message. */
  heartbeat?: (args: { chatId: string }) => JSX.Element;
  /** Follow-up suggestion chips below a message. */
  followUps?: (args: { chatId: string; messageId: string; isLastMessage: boolean }) => JSX.Element;

  /** Copy/download actions under a summary/collaborate activity (bluesky reports). */
  activityMarkdownActions?: (args: { markdown: string; title: string }) => JSX.Element;
  /**
   * Actions for the answer itself, rendered inside the answer block group so
   * they sit against the last line of the text rather than below the completion
   * footer and the follow-up chips. bluesky keeps its own answer action row in
   * its renderer and omits this; chief fills it.
   */
  answerActions?: (args: { messageId: string; chatId: string }) => JSX.Element;
  /** Rich report card for a report_generation operation (bluesky's ReportRenderer). */
  reportCard?: (args: { blockId: string }) => JSX.Element;
}

export interface ChatRuntime {
  store: ChatStoreAccessor;
  actions: ChatActions;
  /**
   * i18n lookup. bluesky passes `useI18n().t`; chief passes an inline English
   * map `(k) => EN[k] ?? k`. Keys are always static literals, so the set a
   * renderer needs is enumerable.
   */
  t: (key: string, params?: Record<string, string>) => string;
  caps: ChatCapabilities;
  /** App-supplied block renderer map the shared `BlockRenderer` dispatches through. */
  blocks: BlockRegistry;
  /** Optional render slots for app-only widgets (bluesky fills; chief omits). */
  slots?: ChatSlots;
  ui?: ChatUiHooks;
}

const ChatRuntimeContext = createContext<ChatRuntime>();

export const ChatRuntimeProvider = (props: { runtime: ChatRuntime; children: JSX.Element }): JSX.Element => {
  // Markdown anywhere in the chat — answers and tool cards alike — copies its
  // code blocks through the host's writer when it has one.
  return (
    <ChatRuntimeContext.Provider value={props.runtime}>
      <MarkdownClipboardProvider copyText={props.runtime.actions.copyText}>{props.children}</MarkdownClipboardProvider>
    </ChatRuntimeContext.Provider>
  );
};

export const useChatRuntime = (): ChatRuntime => {
  const runtime = useContext(ChatRuntimeContext);
  if (!runtime) {
    throw new Error("useChatRuntime must be used within a <ChatRuntimeProvider>");
  }
  return runtime;
};
